
import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { GenerateInspectionReport } from '@/components/admin/documents/inspection/GenerateInspectionReport';
import { EnhancedExportDialog } from '@/components/admin/documents/export/EnhancedExportDialog';
import { ContextualHelp } from '@/components/admin/ContextualHelp';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useReportDeadlines } from '@/hooks/useReportDeadlines';
import { useRetentionExport } from '@/hooks/useRetentionExport';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CertificateTrackingDashboard } from '@/components/admin/certificates/CertificateTrackingDashboard';
import { 
  AlertCircle, 
  Calendar, 
  CheckCircle,
  ClipboardCheck, 
  FileCheck, 
  FileDown, 
  Filter, 
  FolderSearch,
  InfoIcon,
  RefreshCw
} from 'lucide-react';

export default function ComplianceDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const { 
    deadlines, 
    isLoading: isLoadingDeadlines, 
    generateReport 
  } = useReportDeadlines();
  
  const { exportMutation, isExporting } = useRetentionExport();
  
  // Query for certificate reporting stats
  const { data: complianceStats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['certificateComplianceStats'],
    queryFn: async () => {
      try {
        // Get total certificates
        const { data: totalCertificates, error: totalError } = await supabase
          .from('certificate_number_inventory')
          .select('id', { count: 'exact' });
          
        if (totalError) throw totalError;
        
        // Get unreported certificates
        const { data: unreportedCerts, error: unreportedError } = await supabase
          .from('certificate_number_inventory')
          .select('id', { count: 'exact' })
          .eq('is_used', true)
          .eq('reporting_status', 'unreported');
          
        if (unreportedError) throw unreportedError;
        
        // Get pending verifications
        const { data: pendingVerifications, error: pendingError } = await supabase
          .from('certificate_number_inventory')
          .select('id', { count: 'exact' })
          .eq('is_used', true)
          .eq('verification_status', 'unverified');
          
        if (pendingError) throw pendingError;
        
        // Get completed reports
        const { data: completedReports, error: completedError } = await supabase
          .from('certificate_reports')
          .select('id', { count: 'exact' })
          .eq('status', 'completed');
          
        if (completedError) throw completedError;
        
        // Get pending reports
        const { data: pendingReports, error: pendingReportsError } = await supabase
          .from('certificate_reports')
          .select('id', { count: 'exact' })
          .eq('status', 'draft');
          
        if (pendingReportsError) throw pendingReportsError;
        
        // Calculate compliance score
        const totalUsed = totalCertificates?.length || 0;
        const unreported = unreportedCerts?.length || 0;
        const pendingVerify = pendingVerifications?.length || 0;
        
        let complianceScore = 100;
        if (totalUsed > 0) {
          const reportingCompliance = (totalUsed - unreported) / totalUsed;
          const verificationCompliance = (totalUsed - pendingVerify) / totalUsed;
          complianceScore = Math.round(((reportingCompliance + verificationCompliance) / 2) * 100);
        }
        
        return {
          totalCertificates: totalUsed,
          unreportedCertificates: unreported,
          upcomingDeadlines: deadlines?.length || 0,
          pendingVerifications: pendingVerify,
          complianceScore,
          completedReports: completedReports?.length || 0,
          pendingReports: pendingReports?.length || 0
        };
      } catch (error) {
        console.error('Error fetching compliance stats:', error);
        return {
          totalCertificates: 0,
          unreportedCertificates: 0,
          upcomingDeadlines: 0,
          pendingVerifications: 0,
          complianceScore: 0,
          completedReports: 0,
          pendingReports: 0
        };
      }
    },
    enabled: !isLoadingDeadlines
  });
  
  // Query for state compliance info
  const { data: stateCompliance, isLoading: isLoadingStateData } = useQuery({
    queryKey: ['stateComplianceData'],
    queryFn: async () => {
      try {
        // Get data for each state
        const states = ['FL', 'TX', 'CA', 'NY'];
        const stateData = [];
        
        for (const state of states) {
          // Get total reported certificates for this state
          const { data: reported, error: reportedError } = await supabase
            .from('certificate_number_inventory')
            .select('id', { count: 'exact' })
            .eq('state', state)
            .eq('reporting_status', 'reported');
            
          if (reportedError) throw reportedError;
          
          // Get total unreported certificates for this state
          const { data: unreported, error: unreportedError } = await supabase
            .from('certificate_number_inventory')
            .select('id', { count: 'exact' })
            .eq('state', state)
            .eq('is_used', true)
            .eq('reporting_status', 'unreported');
            
          if (unreportedError) throw unreportedError;
          
          // Get next deadline for this state
          let nextDeadline = null;
          const stateDeadlines = deadlines?.filter(d => d.state === state) || [];
          if (stateDeadlines.length > 0) {
            nextDeadline = stateDeadlines[0].dueDate.toISOString().split('T')[0];
          } else {
            // If no specific deadline, set a default one for demo purposes
            const today = new Date();
            const nextQuarterEnd = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3 + 3, 0);
            nextDeadline = nextQuarterEnd.toISOString().split('T')[0];
          }
          
          // Determine status based on unreported count
          let status = 'good';
          if (unreported?.length > 20) {
            status = 'critical';
          } else if (unreported?.length > 5) {
            status = 'warning';
          }
          
          stateData.push({
            state,
            reported: reported?.length || 0,
            unreported: unreported?.length || 0,
            nextDeadline,
            status
          });
        }
        
        return stateData;
      } catch (error) {
        console.error('Error fetching state compliance data:', error);
        return [];
      }
    },
    enabled: !isLoadingDeadlines
  });
  
  // Query for recent reports
  const { data: recentReports, isLoading: isLoadingReports } = useQuery({
    queryKey: ['recentReports'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('certificate_reports')
          .select('*')
          .order('generated_at', { ascending: false })
          .limit(4);
          
        if (error) throw error;
        
        return data?.map(report => ({
          id: report.id,
          title: `Q${report.quarter} ${report.state} ${report.year} Report`,
          state: report.state,
          date: report.generated_at,
          status: report.status
        })) || [];
      } catch (error) {
        console.error('Error fetching recent reports:', error);
        return [];
      }
    }
  });
  
  const handleExport = (exportConfig: Record<string, any>) => {
    exportMutation.mutate({
      state: exportConfig.state,
      startDate: exportConfig.startDate,
      endDate: exportConfig.endDate,
      format: exportConfig.format,
      includeInactive: exportConfig.includeInactive,
      filterByRetentionYears: exportConfig.retentionYears
    });
  };
  
  const handleGenerateReport = async (state: string, year: number, quarter: number) => {
    try {
      toast({
        title: "Report Generation Started",
        description: `Generating report for ${state} Q${quarter} ${year}. This may take a moment...`,
      });
      
      // Call the mutate function from useReportDeadlines
      await generateReport({
        state,
        year,
        quarter
      });
      
      // Show success toast
      toast({
        title: "Report Generation Complete",
        description: `The report for ${state} Q${quarter} ${year} has been generated and saved. View it in the reports section.`,
      });
      
      // Navigate to reports page
      navigate('/admin/certificates/reports');
    } catch (error) {
      console.error("Error generating report:", error);
      toast({
        variant: "destructive",
        title: "Report Generation Failed",
        description: "Failed to generate the report. Please try again.",
      });
    }
  };
  
  const navigateToReports = () => {
    navigate('/admin/certificates/reports');
  };
  
  const openVerificationModule = () => {
    setActiveTab('verification');
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good': return 'text-green-600';
      case 'warning': return 'text-amber-600';
      case 'critical': return 'text-red-600';
      case 'submitted': return 'text-blue-600';
      case 'completed': return 'text-green-600';
      case 'draft': return 'text-amber-600';
      case 'rejected': return 'text-red-600';
      default: return 'text-slate-600';
    }
  };
  
  const isLoading = isLoadingDeadlines || isLoadingStats || isLoadingStateData || isLoadingReports;

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Compliance Dashboard</h1>
            <p className="text-muted-foreground">
              Monitor certificate compliance, generate reports, and track regulatory submissions
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ContextualHelp contextId="compliance" size="lg" />
            <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            <Button size="sm" onClick={() => setIsExportDialogOpen(true)}>
              <FileDown className="h-4 w-4 mr-1" />
              Export
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-primary" />
                Compliance Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Compliance Score:</span>
                  <div className="flex items-center">
                    <span className="font-bold text-lg text-primary">
                      {isLoading ? '—' : complianceStats?.complianceScore + '%'}
                    </span>
                    <CheckCircle className="h-4 w-4 ml-1 text-green-600" />
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Total Certificates:</span>
                  <span className="font-medium">
                    {isLoading ? '—' : complianceStats?.totalCertificates}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Unreported:</span>
                  <span className="font-medium text-amber-600">
                    {isLoading ? '—' : complianceStats?.unreportedCertificates}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Pending Verification:</span>
                  <span className="font-medium text-amber-600">
                    {isLoading ? '—' : complianceStats?.pendingVerifications}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Upcoming Deadlines:</span>
                  <span className="font-medium text-red-600">
                    {isLoading ? '—' : complianceStats?.upcomingDeadlines}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Upcoming Deadlines
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-4 text-muted-foreground">
                  Loading deadlines...
                </div>
              ) : deadlines?.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  No upcoming deadlines
                </div>
              ) : (
                <div className="space-y-3">
                  {deadlines?.slice(0, 2).map((deadline) => (
                    <div key={deadline.id} className="flex justify-between items-center">
                      <div>
                        <div className="font-medium text-sm">
                          {deadline.state} Q{deadline.quarter} {deadline.year} Report
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <span>{deadline.state}</span>
                          <span>•</span>
                          <span>Due: {new Date(deadline.dueDate).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className={`text-sm ${
                        deadline.status === 'overdue' 
                          ? 'text-red-600' 
                          : deadline.status === 'due' 
                            ? 'text-amber-600' 
                            : 'text-blue-600'
                      }`}>
                        {Math.ceil((new Date(deadline.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))} days left
                      </div>
                    </div>
                  ))}
                  {deadlines?.length > 2 && (
                    <Button variant="link" onClick={navigateToReports} className="w-full">
                      View all {deadlines.length} deadlines
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <FolderSearch className="h-5 w-5 text-primary" />
                Recent Reports
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-4 text-muted-foreground">
                  Loading reports...
                </div>
              ) : recentReports?.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  No recent reports
                </div>
              ) : (
                <div className="space-y-3">
                  {recentReports?.slice(0, 3).map(report => (
                    <div key={report.id} className="flex justify-between items-center">
                      <div>
                        <div className="font-medium text-sm">{report.title}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <span>{report.state}</span>
                          <span>•</span>
                          <span>{new Date(report.date).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className={`text-xs px-2 py-1 rounded-full ${
                        report.status === 'draft' ? 'bg-blue-50 text-blue-700' :
                        report.status === 'completed' ? 'bg-green-50 text-green-700' :
                        'bg-red-50 text-red-700'
                      }`}>
                        {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                      </div>
                    </div>
                  ))}
                  {recentReports?.length > 3 && (
                    <Button variant="link" onClick={navigateToReports} className="w-full">
                      View all reports
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">State Overview</TabsTrigger>
            <TabsTrigger value="inspection">Generate Inspection Report</TabsTrigger>
            <TabsTrigger value="verification">Certificate Verification</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>State Compliance Status</CardTitle>
                <CardDescription>
                  Overview of certificate reporting status by state
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Loading state data...
                  </div>
                ) : (
                  <div className="space-y-4">
                    {stateCompliance?.map(state => (
                      <div key={state.state} className="border rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <div className="font-medium">{state.state}</div>
                          <div className={`text-sm font-medium ${getStatusColor(state.status)}`}>
                            {state.status === 'good' && 'Good Standing'}
                            {state.status === 'warning' && 'Needs Attention'}
                            {state.status === 'critical' && 'Critical - Action Required'}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="text-sm text-muted-foreground">Reported Certificates</div>
                            <div className="text-lg font-medium">{state.reported}</div>
                          </div>
                          <div>
                            <div className="text-sm text-muted-foreground">Unreported Certificates</div>
                            <div className={`text-lg font-medium ${state.unreported > 20 ? 'text-red-600' : 'text-amber-600'}`}>
                              {state.unreported}
                            </div>
                          </div>
                        </div>
                        <div className="mt-2">
                          <div className="text-sm text-muted-foreground">Next Report Deadline</div>
                          <div className="flex justify-between items-center">
                            <div className="text-sm">{new Date(state.nextDeadline).toLocaleDateString()}</div>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleGenerateReport(
                                state.state, 
                                new Date().getFullYear(), 
                                Math.floor(new Date().getMonth() / 3) + 1
                              )}
                            >
                              Generate Report
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="inspection">
            <GenerateInspectionReport 
              onReportGenerated={(reportUrl) => {
                console.log('Report generated:', reportUrl);
                toast({
                  title: "Inspection Report Generated",
                  description: "Your inspection report is ready for download.",
                });
              }}
            />
          </TabsContent>
          
          <TabsContent value="verification">
            <CertificateTrackingDashboard />
          </TabsContent>
        </Tabs>
        
        <EnhancedExportDialog 
          open={isExportDialogOpen}
          onOpenChange={setIsExportDialogOpen}
          onExport={handleExport}
        />
      </div>
    </AdminLayout>
  );
}
